import * as THREE from 'three';
import { makeWaveSet, sampleBoatPose, Voyage, type BoatPose, type Wave } from '@sea/world';
import { Boat } from './engine/Boat';
import { CameraRig } from './engine/CameraRig';
import { LAYER_SELF } from './engine/AvatarBody';
import { IslandField } from './engine/Islands';
import { loadTreeGeometries } from './engine/treeAssets';
import { Ocean } from './engine/Ocean';
import { Sky } from './engine/Sky';
import { LocalPlayer } from './game/LocalPlayer';
import { RemotePlayer } from './game/RemotePlayer';
import { NetClient, randomRoomCode, type NetStatus } from './net/NetClient';
import { VoiceChat, type VoiceRole, type VoiceState } from './net/VoiceChat';
import { loadAvatar, type AvatarInstance } from './avatar/VrmAvatar';
import { InputHub } from './platform/input';
import { initHud } from './ui/hud';
import { mountTouchControls } from './ui/touch';
import { Bgm } from './audio/Bgm';
import { ChatBubble } from './ui/ChatBubble';
import { VrKeyboard } from './ui/VrKeyboard';
import { SelfMirror } from './engine/Mirror';
import { VrPanel } from './ui/vrPanel';
import { XRInput } from './platform/xrInput';
import { XRControllerModelFactory } from 'three/examples/jsm/webxr/XRControllerModelFactory.js';
import { XRHandModelFactory } from 'three/examples/jsm/webxr/XRHandModelFactory.js';
import { FOG, state } from './state';

const canvas = document.getElementById('app') as HTMLCanvasElement | null;
if (!canvas) throw new Error('找不到 #app 画布');

const renderer = new THREE.WebGLRenderer({
  canvas,
  antialias: true,
  powerPreference: 'high-performance',
});
renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
renderer.outputColorSpace = THREE.SRGBColorSpace;

const scene = new THREE.Scene();
const sky = new Sky();
scene.add(sky.mesh);

/** 雾色必须与天空地平线同值，否则海的边缘会露出一道缝（§3.2） */
const fogColor = new THREE.Color('#9a958c');
scene.fog = new THREE.Fog(fogColor, FOG.near, FOG.far);

let waves: Wave[] = makeWaveSet(state.seed, state.seaState);

const ocean = new Ocean(waves, {
  fogNear: FOG.near,
  fogFar: FOG.far,
  fogColor,
  sunDirection: sky.sunDirection,
  sunColor: sky.sunColor,
});
scene.add(ocean.group);

/** 吃水：甲板必须高于波峰，否则水会从甲板上「长」出来把船淹掉 */
const BOAT_DRAFT = 0.5;

/** 船体在水平面的投影（略小于轮廓，保证船舷盖住挖洞边缘） */
const HOLE = { x: 0, z: 0, rx: 2.02, rz: 0.6 } as const;
ocean.setHole(HOLE.x, HOLE.z, HOLE.rx, HOLE.rz);

/* ------------------------------------------------------------------ *
 * 场景图（M1 起）：
 *   scene
 *   └ boatTilt            船体真实姿态 heave/pitch/roll
 *      ├ boat.group       船壳与甲板
 *      └ player.root      船局部坐标（走动被 clamp 在甲板内）
 *         ├ body.root     只进 SELF 层的身体
 *         └ rig.root      舒适度补偿（反向旋转）→ camera
 * ------------------------------------------------------------------ */
const boatTilt = new THREE.Group();
const boat = new Boat();
boatTilt.add(boat.group);

/** 触屏端不做指针锁，用拖动；桌面端点击画面锁定鼠标（标准 FPS 手感） */
const coarse = window.matchMedia?.('(pointer: coarse)').matches ?? false;
const forceTouch = new URLSearchParams(window.location.search).has('touch');
const useTouch = coarse || forceTouch;

const rig = new CameraRig(canvas, { pointerLock: !useTouch });
rig.camera.layers.enable(LAYER_SELF); // 自己的第一人称身体
rig.onLockChange = (locked) => document.body.classList.toggle('locked', locked);

const player = new LocalPlayer(rig, 0);
boatTilt.add(player.root);
scene.add(boatTilt);

/**
 * 手柄对象。位置是"参考系坐标"，而参考系原点 = 玩家的座位脚下，
 * 所以必须挂在 `xrAnchorNode` 下面才能得到正确的世界坐标（和相机同一套换算）。
 */
const xrControllers = [renderer.xr.getController(0), renderer.xr.getController(1)];
for (const controller of xrControllers) player.xrAnchorNode.add(controller);
/**
 * 手柄 / 手的可视模型：直接用 three examples 自带的工厂，不自己建模。
 * - XRControllerModelFactory：从 @webxr-input-profiles 拉对应手柄的官方模型（运行时 CDN）
 * - XRHandModelFactory：手部追踪的内置手网格（无需外部资源）
 * 它们必须挂在 xrAnchor 下，位置才是"参考系坐标"。
 */
const controllerModelFactory = new XRControllerModelFactory();
const handModelFactory = new XRHandModelFactory();
const xrHands: THREE.Object3D[] = [];
let handModelsVisible = false; // 默认不显示手柄/手模型（avatar 的手已经在跟了）

const buildHandModels = (): void => {
  if (xrHands.length > 0) return;
  for (let i = 0; i < 2; i++) {
    const controller = xrControllers[i]!;
    try {
      const model = controllerModelFactory.createControllerModel(controller);
      model.name = 'controller-model';
      model.visible = handModelsVisible;
      controller.add(model);
    } catch (err) {
      console.warn('[vr] 手柄模型加载失败', err);
    }
    const hand = renderer.xr.getHand(i);
    xrHands.push(hand);
    try {
      const model = handModelFactory.createHandModel(hand, 'mesh');
      model.name = 'hand-model';
      model.visible = handModelsVisible;
      hand.add(model);
    } catch (err) {
      console.warn('[vr] 手模型创建失败', err);
    }
    player.xrAnchorNode.add(hand);
  }
};
buildHandModels();

const xrHandLeft = new THREE.Vector3();
const xrHandRight = new THREE.Vector3();
const xrQuatLeft = new THREE.Quaternion();
const xrQuatRight = new THREE.Quaternion();
const xrPoseMatrix = new THREE.Matrix4();
const xrPosePos = new THREE.Vector3();
const xrPoseQuat = new THREE.Quaternion();
const xrPoseScale = new THREE.Vector3(1, 1, 1);

// 校准镜子（three 自带 Reflector）与 VR 状态面板（three-mesh-ui）
// 都包在 try/catch 里：任何一方初始化失败都不能挡住整个应用启动。
type MirrorLike = {
  setVisible(v: boolean): void;
  visible: boolean;
  group: THREE.Object3D;
  update(renderer: THREE.WebGLRenderer, scene: THREE.Scene): void;
  sample(renderer: THREE.WebGLRenderer): [number, number, number] | null;
  previewOverlay(renderer: THREE.WebGLRenderer): string;
  onBeforeRender: (() => void) | null;
  onAfterRender: (() => void) | null;
};
type PanelLike = VrPanel;

let mirror: MirrorLike | null = null;
try {
  const sm = new SelfMirror();
  boatTilt.add(sm.group);
  mirror = sm;
} catch (err) {
  console.warn('[vr] 镜子初始化失败', err);
}

let panelError: string | null = null;
let vrKeyboard: VrKeyboard | null = null;
let vrPanel: PanelLike | null = null;
try {
  const vp = new VrPanel({
    toggleSit: () => inputHub.pressSit(),
    toggleMute: () => {
      if (!voice) return;
      voice.setMuted(!voice.muted);
      hud.setVoice(voice.muted ? '语音：已静音' : '语音：通话中');
    },
    openChat: () => openXrChat(),
    toggleVoice: () => {
      if (voice) {
        voice.setMuted(!voice.muted);
        hud.setVoice(voice.muted ? '语音：已静音' : '语音：通话中');
        selfBubble?.show(voice.muted ? '已静音' : '已开麦');
        return;
      }
      const peer = [...remotes.keys()][0];
      if (!peer) {
        selfBubble?.show('对方还没进房间');
        return;
      }
      startVoice(peer);
      selfBubble?.show('正在建立语音…');
    },
    quickChat: (text) => {
      hud.addChat(text, true);
      selfBubble?.show(text);
      net.sendChat(text);
    },
    copyInvite: () => {
      const url = location.origin + location.pathname + '?room=' + roomCode;
      void navigator.clipboard?.writeText(url).then(
        () => hud.addChat('（邀请链接已复制）', true),
        () => hud.addChat('（复制失败，请用电脑打开复制）', true),
      );
    },
    toggleOcean: () => {
      const next = bgm.set({ ocean: !bgm.current.ocean });
      hud.setBgm(next);
    },
    toggleMusic: () => {
      const next = bgm.set({ music: !bgm.current.music });
      hud.setBgm(next);
    },
    toggleMirror: () => {
      if (!mirror) return;
      mirror.setVisible(!mirror.visible);
    },
    adjustCalibration: (key, delta) => {
      const next = { ...vrCal };
      next[key] = (next[key] ?? 0) + delta;
      vrCal = next;
      player.setVRCalibration(vrCal);
      saveVRCal(vrCal);
      hud.setVRCalibration(vrCal as never);
    },
    resetCalibration: () => {
      vrCal = { ...DEFAULT_CAL };
      player.setVRCalibration(vrCal);
      saveVRCal(vrCal);
      hud.setVRCalibration(vrCal as never);
    },
  });
  // 挂在玩家身上、悬在身前：不会被桅杆挡住，走到哪跟到哪
  vp.setVisible(false); // 只在 VR 里显示
  player.root.add(vp.group);
  vp.group.position.set(0.3, 0.98, -1.15);
vp.group.scale.setScalar(0.85);
  vrPanel = vp;

  // 3D 键盘（DOM Overlay 不可用时的替代）
  const kb = new VrKeyboard((text) => {
    hud.addChat(text, true);
    selfBubble?.show(text);
    net.sendChat(text);
  });
  player.root.add(kb.group);
  vrKeyboard = kb;
} catch (err) {
  console.warn('[vr] 状态面板初始化失败', err);
  panelError = String(err);
}

(window as unknown as { __vrInit: unknown }).__vrInit = {
  mirror: !!mirror,
  panel: !!vrPanel,
  panelError,
};

const bgm = new Bgm();

const inputHub = new InputHub();

let voyage = new Voyage({ seed: state.seed, firstAt: 110 });
const islands = new IslandField(12);
scene.add(islands.group);

// 树用 Kenney Nature Kit（CC0）的低模 GLB：启动时加载一次，烘成单几何体后交给岛屿的 InstancedMesh。
// 失败就退回程序化锥体树，不影响启动。
void loadTreeGeometries().then((geometries) => {
  if (!geometries) return;
  islands.setTreeGeometries(geometries);
  console.info('[islands] 已载入 GLB 树资产');
});

const sun = new THREE.DirectionalLight(0xffd7a8, 2.0);
sun.position.copy(sky.sunDirection).multiplyScalar(300);
scene.add(sun);
// 半球光要够亮：岛与船的大部分面是背光的，纯靠方向光会黑成一团剪影
scene.add(new THREE.HemisphereLight(0x9fc7ff, 0x6a7681, 1.35));

function rebuildVoyage(seed: number): void {
  voyage = new Voyage({ seed, firstAt: 110 });
  state.distance = 0;
}

/* ---------------- 联机 + 头像 ---------------- *
 * 启动顺序：先拿到 manifest（本地玩家要用同一个模型），再接 WebSocket。
 * 世界是确定性的，所以网络上只跑两个人的姿态，没有"世界状态"。
 */
type AvatarEntry = { id: string; file: string; name?: string };

let avatar: AvatarInstance | null = null;
/** 自己头顶的聊天气泡 */
let selfBubble: ChatBubble | null = null;
/** 对方头顶的聊天气泡（按 id） */
const remoteBubbles = new Map<string, ChatBubble>();
let avatarEntry: AvatarEntry | null = null;

const params = new URLSearchParams(window.location.search);
const roomCode = (params.get('room') ?? randomRoomCode()).toUpperCase();

function avatarUrlFor(entry: AvatarEntry): string {
  return `/avatars/${entry.id}/${entry.file}`;
}

async function resolveAvatarEntry(): Promise<AvatarEntry | null> {
  let entries: AvatarEntry[] = [];
  try {
    const res = await fetch('/avatars/manifest.json');
    const json = (await res.json()) as { avatars?: AvatarEntry[] };
    entries = json.avatars ?? [];
  } catch {
    /* 没有 manifest 就退化成程序化身体 */
  }
  const wanted = params.get('avatar');
  return wanted ? (entries.find((e) => e.id === wanted) ?? null) : (entries[0] ?? null);
}

async function attachSelfAvatar(entry: AvatarEntry): Promise<void> {
  try {
    const loaded = await loadAvatar(avatarUrlFor(entry), { targetHeight: 1.62 });
    player.attachAvatar(loaded);
    avatar = loaded;
    selfBubble = new ChatBubble();
    selfBubble.attachTo(loaded.root, 1.95);
    console.info('[avatar] 已加载', entry.id, loaded.measure);
  } catch (err) {
    console.warn('[avatar] 加载失败：', err);
  }
}

/* ---------------- 联机 ---------------- */
const net = new NetClient();
const remotes = new Map<string, RemotePlayer>();

/* ---------------- 语音 ---------------- */
let voice: VoiceChat | null = null;
/** 本次 VR 会话是否真的拿到了 DOM Overlay（Quest 浏览器支持，桌面 Chrome 不支持） */
let domOverlaySupported = false;
let voicePeerId = '';
let lastMouthSent = 0;
let lastMouthLevel = 0;

/* ---------------- 击掌 ---------------- */
/** 我还在举手的截止时间（performance.now） */
let palmUntil = 0;
/** 对方还在举手的截止时间 */
let peerPalmUntil = 0;
/** 击掌冷却，避免同一个窗口里疯狂触发 */
let clapCooldown = 0;
const handA = new THREE.Vector3();
const handB = new THREE.Vector3();

function voiceLabel(state: VoiceState, detail?: string): string {
  switch (state) {
    case 'requesting-mic':
      return '语音：请求麦克风…';
    case 'connecting':
      return '语音：连接中…';
    case 'live':
      return '语音：通话中';
    case 'failed':
      return detail === 'NotAllowedError' ? '语音：麦克风被拒' : `语音：不可用`;
    default:
      return '语音：关';
  }
}

function startVoice(peerId: string): void {
  if (voice || !peerId || !net.selfId) return;
  voicePeerId = peerId;
  // 两边都用自生成的 id，按字典序决定谁发 offer，保证只发一次
  const role: VoiceRole = net.selfId < peerId ? 'offerer' : 'answerer';
  voice = new VoiceChat((msg) => net.sendSignal(msg), role, params.has('fakemic'));
  voice.onState = (state, detail) => {
    hud.setVoice(voiceLabel(state, detail));
    // VR 里看不到 DOM，用气泡把关键状态报出来
    if (state === 'live') selfBubble?.show('语音已接通');
    else if (state === 'failed') selfBubble?.show(detail === 'NotAllowedError' ? '麦克风被拒绝' : '语音建立失败');
  };
  hud.setVoice('语音：请求麦克风…');
  void voice.start();
}

function stopVoice(): void {
  voice?.close();
  voice = null;
  voicePeerId = '';
  hud.setVoice('语音：关');
}

function roomStateText(): string {
  const status: NetStatus = net.status;
  if (status === 'connecting') return '连接中…';
  if (status === 'full') return '房间已满';
  if (status === 'error') return '连接出错';
  if (status === 'closed') return '重连中…';
  if (status === 'open') return remotes.size > 0 ? '对面已上船' : '等待对方…';
  return '未连接';
}

function refreshRoomHud(): void {
  hud.setRoom(roomCode, roomStateText());
}

async function startNetworking(): Promise<void> {
  hud.setRoom(roomCode, '连接中…');

  net.onStatus = () => refreshRoomHud();

  // 进房后按座位号落座，避免两人叠在同一个位子上
  net.onWelcome = () => {
    player.sit(net.joinSeat);
    refreshRoomHud();
    const first = [...net.peers.keys()][0];
    if (first) startVoice(first);
  };

  net.onSignal = (from, msg) => {
    if (from === voicePeerId) void voice?.handleSignal(msg as never);
  };

  net.onMouth = (id, value) => {
    remotes.get(id)?.setMouth(value);
  };

  net.onPeerJoin = (peer) => {
    if (remotes.has(peer.id)) return;
    const remote = new RemotePlayer(peer.id);
    boatTilt.add(remote.root);
    const bubble = new ChatBubble();
    bubble.attachTo(remote.root, 1.95);
    remoteBubbles.set(peer.id, bubble);
    remotes.set(peer.id, remote);
    refreshRoomHud();
    startVoice(peer.id);

    if (avatarEntry) {
      const entry = avatarEntry;
      void remote
        .load(avatarUrlFor(entry))
        .then(() => console.info('[net] 远端模型就绪', peer.id))
        .catch((err) => console.warn('[net] 远端模型加载失败', err));
    }
  };

  net.onPeerLeave = (id) => {
    remotes.get(id)?.dispose();
    remotes.delete(id);
    if (id === voicePeerId) stopVoice();
    refreshRoomHud();
  };

  net.onPose = (id, pose) => {
    remotes.get(id)?.setPose(pose);
  };

  net.onExpr = (id, name) => {
    remotes.get(id)?.playExpression(name);
  };

  net.onGesture = (id, name) => {
    remotes.get(id)?.playGesture(name);
    if (name === 'palm') peerPalmUntil = performance.now() + 2600;
  };

  net.onChat = (id, text) => {
    hud.addChat(text, false);
    remoteBubbles.get(id)?.show(text);
  };

  net.connect(roomCode, { avatar: avatarEntry?.id ?? '' });

  // 姿态上行用**定时器**而不是渲染循环：标签页切到后台时 rAF 会被暂停，
  // 但 WebSocket 与定时器还在（会被节流到 ~1 Hz，但至少对方不会看到你整个人冻住）。
  window.setInterval(() => {
    net.sendPose(
      {
        x: player.root.position.x,
        z: player.root.position.z,
        yaw: rig.heading,
        seated: player.isSeated,
      },
      performance.now(),
    );
  }, 45); // 略小于内部 50 ms 节流，实际约 20 Hz，避免定时器抖动把包全卡掉
}

/** 数字键 1–5 触发表情，F 挥手；本地立刻播，同时发给对方 */
const EXPRESSION_KEYS: Record<string, string> = {
  Digit1: 'happy',
  Digit2: 'angry',
  Digit3: 'sad',
  Digit4: 'relaxed',
  Digit5: 'surprised',
};

function bindInteractionKeys(): void {
  window.addEventListener('keydown', (e) => {
    if (e.repeat) return;
    const name = EXPRESSION_KEYS[e.code];
    if (name) {
      avatar?.pulseExpression(name);
      net.sendExpr(name);
      return;
    }
    if (e.code === 'KeyF') {
      avatar?.pulseGesture('wave');
      net.sendGesture('wave');
      return;
    }
    if (e.code === 'KeyG') {
      avatar?.pulseGesture('palm');
      net.sendGesture('palm');
      palmUntil = performance.now() + 2600;
      hud.addChat('（举手，等对方）', true);
      return;
    }
    if (e.code === 'KeyK') {
      player.calibrateXRHeight();
      hud.addChat('（身高重新校准：站好别动）', true);
      return;
    }
    if (e.code === 'KeyH') {
      hud.togglePanel();
      return;
    }
    if (e.code === 'KeyR') {
      if (!mirror) return;
      mirror.setVisible(!mirror.visible);
      hud.addChat(mirror.visible ? '（镜子已打开）' : '（镜子已关闭）', true);
      return;
    }
    if (e.code === 'KeyP') {
      if (!vrPanel) return;
      vrPanel.setVisible(!vrPanel.group.visible);
      return;
    }
    if (e.code === 'KeyM' && voice) {      const next = !voice.muted;
      voice.setMuted(next);
      hud.setVoice(next ? '语音：已静音' : '语音：通话中');
    }
  });
}

async function bootstrap(): Promise<void> {
  avatarEntry = await resolveAvatarEntry();
  // 校准值先塞进去，接上模型时会立刻生效
  player.setVRCalibration(vrCal);
  if (avatarEntry) await attachSelfAvatar(avatarEntry);
  bindInteractionKeys();
  // 浏览器自动播放策略：任何一次点击后再尝试把对端音频放出来
  window.addEventListener('pointerdown', () => voice?.resumeAudio());
  await startNetworking();
}

void bootstrap();

/* ---------------- VR 校准（本地持久化）---------------- */
// 版本号进 key：校准语义变了（手腕默认 90°、身高补偿系数调整），旧存档不能复用
const VR_CAL_KEY = 'sea.vrCal.v12';
const DEFAULT_CAL = {
  scale: 1.1,
  reach: 1.5,
  wristDeg: 20,
  tiltDeg: 60,
  yawDeg: 180,
  offsetX: 0,
  offsetY: 0.2,
  offsetZ: 0,
  curlFlip: true,
  elbowFlip: false,
  headFlip: false,
};
type VRCal = typeof DEFAULT_CAL;

function loadVRCal(): VRCal {
  try {
    const raw = localStorage.getItem(VR_CAL_KEY);
    if (!raw) return { ...DEFAULT_CAL };
    return { ...DEFAULT_CAL, ...(JSON.parse(raw) as Partial<VRCal>) };
  } catch {
    return { ...DEFAULT_CAL };
  }
}

function saveVRCal(cal: VRCal): void {
  try {
    localStorage.setItem(VR_CAL_KEY, JSON.stringify(cal));
  } catch {
    /* 隐私模式下写不进去，忽略 */
  }
}

let vrCal: VRCal = loadVRCal();

// 浏览器要求用户先交互过才能播音频：任意一次指针/按键都启动
const armAudio = (): void => {
  bgm.start();
  window.removeEventListener('pointerdown', armAudio);
  window.removeEventListener('keydown', armAudio);
};
window.addEventListener('pointerdown', armAudio);
window.addEventListener('keydown', armAudio);

const hud = initHud({
  onMotion: (v) => {
    state.motion = v;
  },
  onSpeed: (v) => {
    state.speed = v;
  },
  onSea: (seaState) => {
    state.seaState = seaState;
    waves = makeWaveSet(state.seed, seaState);
    ocean.setWaves(waves);
  },
  onSeed: (seed) => {
    state.seed = seed;
    waves = makeWaveSet(seed, state.seaState);
    ocean.setWaves(waves);
    rebuildVoyage(seed);
  },
  onSit: () => inputHub.pressSit(),
  onCalibrate: () => {
    player.calibrateXRHeight();
    hud.addChat('（身高重新校准：站好别动）', true);
  },
  onVRCalibration: (partial) => {
    vrCal = { ...vrCal, ...partial };
    player.setVRCalibration(vrCal);
    saveVRCal(vrCal);
  },
  onVRCalibrationReset: () => {
    vrCal = { ...DEFAULT_CAL };
    player.setVRCalibration(vrCal);
    saveVRCal(vrCal);
  },
  onVoiceToggle: () => {
    // 已经建立通话 → 切换静音
    if (voice) {
      voice.setMuted(!voice.muted);
      hud.setVoice(voice.muted ? '语音：已静音' : '语音：通话中');
      return;
    }
    // 还没通话：有对方就直接起，没对方就先申请麦克风权限
    const peer = [...remotes.keys()][0];
    if (peer) {
      startVoice(peer);
      return;
    }
    void navigator.mediaDevices
      ?.getUserMedia({ audio: true })
      .then((stream) => {
        for (const track of stream.getTracks()) track.stop();
        hud.setVoice('语音：麦克风已就绪（等对方进房间）');
      })
      .catch((err: unknown) => {
        const detail = err instanceof DOMException ? err.name : String(err);
        hud.setVoice(detail === 'NotAllowedError' ? '语音：麦克风被拒' : '语音：麦克风不可用');
      });
  },
  onBgm: (patch) => {
    bgm.set(patch);
  },
  onChat: (text) => {
    if (!text.trim()) return;
    hud.addChat(text, true);
    selfBubble?.show(text);
    net.sendChat(text);
  },
});

// 把存档里的校准值同步到滑块
hud.setVRCalibration(vrCal as never);
// 背景音开关也同步一次（存档里可能关掉了）
hud.setBgm(bgm.current);
player.setVRCalibration(vrCal);

/* ---------------- 触屏 ---------------- */
if (useTouch) {
  document.body.classList.add('touch');
  mountTouchControls({
    onMove: (forward, strafe) => inputHub.setMoveAxis(forward, strafe),
    onSit: () => inputHub.pressSit(),
  });
}

/* ---------------- VR 入口（代码路径就位，尚未在头显上验证）---------------- */
type XRSystemLike = {
  isSessionSupported(mode: string): Promise<boolean>;
  requestSession(mode: string, options?: Record<string, unknown>): Promise<XRSession>;
};
const nav = navigator as Navigator & { xr?: XRSystemLike };
const vrButton = document.getElementById('vr') as HTMLButtonElement | null;

if (vrButton && nav.xr) {
  nav.xr
    .isSessionSupported('immersive-vr')
    .then((ok) => {
      if (ok) {
        vrButton.hidden = false;
        // 校准区只在支持 VR 的设备上显示（桌面/手机用不到这些参数）
        document.getElementById('vr-cal')?.removeAttribute('hidden');
      }
    })
    .catch(() => {
      /* 不支持就算了，静默降级 */
    });

  const voicePermBtn = document.getElementById('voice-perm') as HTMLButtonElement | null;
voicePermBtn?.addEventListener('click', async () => {
  voicePermBtn.disabled = true;
  hud.setVoice('语音：请求麦克风…');
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    // 拿到就立刻释放：这一步只是为了把权限"记下来"
    for (const track of stream.getTracks()) track.stop();
    hud.setVoice('语音：麦克风已就绪');
    voicePermBtn.textContent = '语音已就绪';
    // 如果对方已经在房间里，直接开始通话
    const first = [...remotes.keys()][0];
    if (first) startVoice(first);
  } catch (err) {
    const detail = err instanceof DOMException ? err.name : String(err);
    hud.setVoice(detail === 'NotAllowedError' ? '语音：麦克风被拒' : '语音：麦克风不可用');
    voicePermBtn.disabled = false;
  }
});

vrButton.addEventListener('click', async () => {
    if (!nav.xr) return;
    try {
      const overlayRoot = document.getElementById('xr-overlay');
      const session = await nav.xr.requestSession('immersive-vr', {
        optionalFeatures: ['local-floor', 'bounded-floor', 'hand-tracking', 'dom-overlay'],
        // DOM Overlay：VR 里也能显示真 DOM，聚焦 input 会弹系统键盘（Quest 支持）
        domOverlay: overlayRoot ? { root: overlayRoot } : undefined,
      });
      // domOverlayState 存在 = 浏览器真的给了 DOM Overlay
      domOverlaySupported = Boolean((session as unknown as { domOverlayState?: unknown }).domOverlayState);
      console.info('[vr] dom-overlay:', domOverlaySupported);
      renderer.xr.enabled = true;
      // 以地板为原点：用户真实身高直接映射到甲板之上
      renderer.xr.setReferenceSpaceType('local-floor');
      await renderer.xr.setSession(session);
      // 相机从脖子挪到稳定挂点，并停用鼠标视角/补偿
      player.enterXR();
      rig.setXRActive(true);
      vrPanel?.setVisible(true);
      document.body.classList.add('xr');
      vrButton.textContent = '退出 VR';
      session.addEventListener('end', () => {
        document.getElementById('xr-overlay')?.setAttribute('hidden', '');
        vrKeyboard?.setVisible(false);
        renderer.xr.enabled = false;
        player.exitXR();
        rig.setXRActive(false);
        vrPanel?.setVisible(false);
        document.body.classList.remove('xr');
        vrButton.textContent = '进入 VR';
      });
    } catch (err) {
      console.warn('进入 VR 失败：', err);
    }
  });
}

/* ---------------- VR 里的文字聊天（DOM Overlay + 系统键盘） ---------------- */
const xrOverlay = document.getElementById('xr-overlay');
const xrChatForm = document.getElementById('xr-chat-form') as HTMLFormElement | null;
const xrChatInput = document.getElementById('xr-chat-input') as HTMLInputElement | null;
const xrChatClose = document.getElementById('xr-chat-close');

/** 在 VR 里打开输入条并聚焦（聚焦会触发 Quest 的系统虚拟键盘） */
function openXrChat(): void {
  // 反馈走气泡（VR 里能看见）。连这条都看不到 → 射线没点到按钮
  selfBubble?.show('打开文字输入');
  if (!xrOverlay) {
    selfBubble?.show('本浏览器不支持 DOM Overlay');
    return;
  }
  if (!domOverlaySupported) {
    // 没有 DOM Overlay 就用自带的 3D 键盘
    selfBubble?.show('打开 3D 键盘');
    vrKeyboard?.setVisible(true);
    return;
  }
  xrOverlay.removeAttribute('hidden');
  // 放到下一帧再 focus，确保 overlay 已经可见
  requestAnimationFrame(() => xrChatInput?.focus());
}

function closeXrChat(): void {
  xrOverlay?.setAttribute('hidden', '');
  xrChatInput?.blur();
}

xrChatForm?.addEventListener('submit', (event) => {
  event.preventDefault();
  const text = xrChatInput?.value.trim() ?? '';
  if (text) {
    hud.addChat(text, true);
    selfBubble?.show(text);
    net.sendChat(text);
    if (xrChatInput) xrChatInput.value = '';
  }
  closeXrChat();
});

xrChatClose?.addEventListener('click', () => closeXrChat());

/* ---------------- 尺寸 ---------------- */
function resize(): void {
  const w = window.innerWidth;
  const h = window.innerHeight;
  renderer.setSize(w, h, false);
  // XR 下投影矩阵由头显的每眼参数决定，别用窗口宽高去覆盖它
  if (!renderer.xr.isPresenting) rig.resize(w, h);
}
window.addEventListener('resize', resize);
resize();

/* ---------------- VR 手柄 ---------------- */
const xrInput = new XRInput();

/**
 * 手部追踪：用相邻指节的方向夹角估算每根手指的弯曲量（0..1）。
 * 手柄没有手指关节，只有手部追踪模式才有这 25 个关节。
 */
function readHandCurls(
  hand: XRHand,
  frame: XRFrame,
  refSpace: XRReferenceSpace,
): { thumb: number; index: number; middle: number; ring: number; little: number } | null {
  const joints = [
    ['thumb', 'thumb-phalanx-proximal', 'thumb-phalanx-distal', 'thumb-tip'],
    ['index', 'index-finger-phalanx-proximal', 'index-finger-phalanx-intermediate', 'index-finger-tip'],
    ['middle', 'middle-finger-phalanx-proximal', 'middle-finger-phalanx-intermediate', 'middle-finger-tip'],
    ['ring', 'ring-finger-phalanx-proximal', 'ring-finger-phalanx-intermediate', 'ring-finger-tip'],
    ['little', 'little-finger-phalanx-proximal', 'little-finger-phalanx-intermediate', 'little-finger-tip'],
  ] as const;

  const pos = (name: string): THREE.Vector3 | null => {
    const joint = hand.get(name as never);
    const pose = joint ? frame.getJointPose?.(joint, refSpace) ?? null : null;
    if (!pose) return null;
    return new THREE.Vector3(
      pose.transform.position.x,
      pose.transform.position.y,
      pose.transform.position.z,
    );
  };

  const out = { thumb: 0, index: 0, middle: 0, ring: 0, little: 0 };
  let got = false;
  for (const [finger, proxName, midName, tipName] of joints) {
    const a = pos(proxName);
    const b = pos(midName);
    const c = pos(tipName);
    if (!a || !b || !c) continue;
    const v1 = b.sub(a).normalize();
    const v2 = c.sub(b).normalize();
    const angle = Math.acos(Math.max(-1, Math.min(1, v1.dot(v2))));
    out[finger] = Math.min(1, angle / (Math.PI * 0.55));
    got = true;
  }
  return got ? out : null;
}


/* ---------------- 主循环 ---------------- */
const pose: BoatPose = { heave: 0, pitch: 0, roll: 0 };
const camPos = new THREE.Vector3();

let last = performance.now();
let lastPanelUpdate = 0;
/** VR 面板的射线拾取 */
const panelRay = new THREE.Raycaster();
const panelRayOrigin = new THREE.Vector3();
const panelRayDir = new THREE.Vector3();
const panelRayQuat = new THREE.Quaternion();
let prevTriggerL = 0;
let prevTriggerR = 0;
/** 当前手势对应的表情（VRChat 的 gesture override：捏/握/指对应不同表情） */
let gestureExpression: string | null = null;
let fpsSmooth = 60;

function frame(now: number, xrFrame?: XRFrame): void {
  const dt = Math.min(0.05, Math.max(0.0001, (now - last) / 1000));
  last = now;
  fpsSmooth = fpsSmooth * 0.92 + (1 / dt) * 0.08;

  state.time += dt;
  state.distance += state.speed * dt;

  // 1) 世界：唯一真相来源，两端各自算
  sampleBoatPose(waves, state.time, undefined, pose);

  // 2) 船体：真实姿态 + 吃水
  boatTilt.position.y = pose.heave + BOAT_DRAFT;
  boatTilt.rotation.x = pose.pitch;
  boatTilt.rotation.z = pose.roll;

  // 3) 相机舒适度：先阻尼，玩家再读它来摆脖子
  rig.applyComfort(pose, state.motion);
  rig.update(dt);

  // 4) 玩家：坐/站 + 甲板走动（船局部坐标，clamp 在甲板内）
  const input = inputHub.sample();
  if (renderer.xr.isPresenting) {
    // VR 手柄：摇杆并进同一套轴，按键转成和桌面一样的边沿事件
    const xr = xrInput.update(renderer as never, (xrFrame ?? null) as never);
    input.forward = Math.max(-1, Math.min(1, input.forward + xr.forward));
    input.strafe = Math.max(-1, Math.min(1, input.strafe + xr.strafe));
    input.sit = input.sit || xr.wave; // A / X 切坐站（扳机留给面板点击）
    if (xr.snapTurn !== 0) player.snapTurn(xr.snapTurn);
    if (xr.palm) {
      // B / Y：开关 VR 面板（原"举手"手势改由 A/X 挥手代替）
      if (vrPanel) {
        vrPanel.setVisible(!vrPanel.group.visible);
        hud.addChat(vrPanel.group.visible ? '（面板已打开）' : '（面板已关闭）', true);
      }
    }
    // 手势 → 表情（VRChat 的 gesture override 思路）
    //   双手全握 → 生气 ；双手只扣扳机 → 惊讶 ；两个都扣 → 开心 ；松开 → 恢复
    const bothGrip = xr.gripLeft > 0.75 && xr.gripRight > 0.75;
    const bothTrigger = xr.triggerLeft > 0.75 && xr.triggerRight > 0.75;
    const nextGesture = bothGrip && bothTrigger ? 'happy' : bothGrip ? 'angry' : bothTrigger ? 'surprised' : null;

    if (nextGesture !== gestureExpression) {
      // 清掉上一个
      if (gestureExpression) avatar?.setExpression(gestureExpression, 0);
      gestureExpression = nextGesture;
      if (gestureExpression) {
        avatar?.setExpression(gestureExpression, 1);
        net.sendExpr(gestureExpression);
      }
    } else if (gestureExpression) {
      // 手势保持期间持续压住（防止被眨眼/口型的 setValue 覆盖回来）
      avatar?.setExpression(gestureExpression, 1);
    }

    // 面板交互：手柄射线 + 扳机拾取（three-mesh-ui 不提供交互，官方示例也是这么做的）
    if (vrPanel && vrPanel.group.visible) {
      const triggerEdgeL = xr.triggerLeft > 0.6 && prevTriggerL <= 0.6;
      const triggerEdgeR = xr.triggerRight > 0.6 && prevTriggerR <= 0.6;
      prevTriggerL = xr.triggerLeft;
      prevTriggerR = xr.triggerRight;

      const targets: Array<{
        objects: THREE.Object3D[];
        handle: (uv: { x: number; y: number } | null, pressed: boolean) => void;
      }> = [{ objects: vrPanel.interactables, handle: (uv, pressed) => vrPanel?.handleHit(uv, pressed) }];
      if (vrKeyboard?.visible) {
        targets.push({
          objects: vrKeyboard.interactables,
          handle: (uv, pressed) => vrKeyboard?.handleHit(uv, pressed),
        });
      }

      const hitTargets = new Set<number>();
      for (let i = 0; i < xrControllers.length; i++) {
        const controller = xrControllers[i];
        if (!controller) continue;
        controller.updateWorldMatrix(true, false);
        controller.getWorldPosition(panelRayOrigin);
        controller.getWorldQuaternion(panelRayQuat);
        panelRayDir.set(0, 0, -1).applyQuaternion(panelRayQuat).normalize();
        panelRay.set(panelRayOrigin, panelRayDir);
        panelRay.far = 3;

        for (let t = 0; t < targets.length; t++) {
          const target = targets[t]!;
          const hit = panelRay.intersectObjects(target.objects, true)[0];
          if (!hit) continue;
          hitTargets.add(t);
          target.handle(hit.uv ? { x: hit.uv.x, y: hit.uv.y } : null, i === 0 ? triggerEdgeL : triggerEdgeR);
        }
      }
      targets.forEach((target, index) => {
        if (!hitTargets.has(index)) target.handle(null, false);
      });
    } else {
      prevTriggerL = xr.triggerLeft;
      prevTriggerR = xr.triggerRight;
    }

    // 手指：手柄没有手指关节，按 VRChat 的约定用模拟量驱动
    //   扳机 → 食指；抓握 → 中指/无名指/小指 + 拇指
    const curlsFor = (trigger: number, grip: number) => ({
      thumb: Math.min(1, grip * 0.7),
      index: Math.max(trigger, grip * 0.55),
      middle: grip * 0.9,
      ring: grip * 0.9,
      little: grip * 0.9,
    });
    avatar?.setFingerCurls('left', curlsFor(xr.triggerLeft, xr.gripLeft));
    avatar?.setFingerCurls('right', curlsFor(xr.triggerRight, xr.gripRight));

    if (xr.mute && voice) {
      voice.setMuted(!voice.muted);
      hud.setVoice(voice.muted ? '语音：已静音' : '语音：通话中');
    }

    // 手柄追踪：直接问 XRFrame 要每只手的世界姿态。
    //
    // 不能再用 `renderer.xr.getController(i)` 配 `inputSources[i]`：Quest 打开系统菜单
    // 再返回后，`inputSources` 的顺序会变，按下标配就把左右手接反了（真机踩到的 bug）。
    // `XRRigidTransform` 跟着 inputSource 走，天然不会串。
    const session = renderer.xr.getSession();
    const refSpace = renderer.xr.getReferenceSpace();
    let hasLeft = false;
    let hasRight = false;
    if (session && xrFrame && refSpace) {
      player.xrAnchorNode.updateWorldMatrix(true, false);
      for (const src of session.inputSources) {
        // 手部追踪优先：它有 25 个关节，既给手的位置也给手指弯曲
        const hand = (src as unknown as { hand?: XRHand }).hand ?? null;
        let pose: XRPose | null = null;

        if (hand) {
          const joint = hand.get('middle-finger-metacarpal');
          const jointPose = joint ? xrFrame.getJointPose?.(joint, refSpace) ?? null : null;
          if (jointPose) {
            pose = {
              transform: jointPose.transform,
            } as unknown as XRPose;
            // 手指：用相邻指节的方向夹角估算弯曲量
            const curls = readHandCurls(hand, xrFrame, refSpace);
            if (curls) {
              avatar?.setFingerCurls(src.handedness === 'left' ? 'left' : 'right', curls);
            }
          }
        }

        if (!pose) {
          const space = src.gripSpace ?? src.targetRaySpace;
          if (!space) continue;
          pose = xrFrame.getPose(space, refSpace) ?? null;
        }
        if (!pose) continue;

        xrPoseMatrix.compose(
          xrPosePos.set(
            pose.transform.position.x,
            pose.transform.position.y,
            pose.transform.position.z,
          ),
          xrPoseQuat.set(
            pose.transform.orientation.x,
            pose.transform.orientation.y,
            pose.transform.orientation.z,
            pose.transform.orientation.w,
          ),
          xrPoseScale,
        );
        // 参考系原点就在 xrAnchor 上，所以再乘它的世界矩阵就是世界姿态
        xrPoseMatrix.premultiply(player.xrAnchorNode.matrixWorld);
        xrPoseMatrix.decompose(xrPosePos, xrPoseQuat, xrPoseScale);

        if (src.handedness === 'left') {
          xrHandLeft.copy(xrPosePos);
          xrQuatLeft.copy(xrPoseQuat);
          hasLeft = true;
        } else if (src.handedness === 'right') {
          xrHandRight.copy(xrPosePos);
          xrQuatRight.copy(xrPoseQuat);
          hasRight = true;
        }
      }
    }
    player.setHandTargets(
      hasLeft || hasRight
        ? {
            left: hasLeft ? { position: xrHandLeft, quaternion: xrQuatLeft } : null,
            right: hasRight ? { position: xrHandRight, quaternion: xrQuatRight } : null,
          }
        : null,
    );
  }
  player.update(dt, input);

  boatTilt.updateMatrixWorld(true);
  rig.camera.getWorldPosition(camPos);
  ocean.update(state.time, camPos);
  sky.update(camPos);

  const visibleIslands = islands.update(voyage, state.distance, 70, FOG.far);
  avatar?.update(dt);

  // VR 状态面板：每 0.5 s 刷新一次，不需要每帧
  if (vrPanel && vrPanel.group.visible && now - lastPanelUpdate > 500) {
    lastPanelUpdate = now;
    const cal = player.xrCalibration;
    vrPanel.update({
      posture: player.isSeated ? '坐' : '站',
      voice: voice ? (voice.muted ? '静音' : '开') : '关',
      net: net.status === 'open' ? roomCode : '未连接',
      mirrorOn: mirror ? mirror.visible : false,
      roomCode: net.status === 'open' ? roomCode : '----',
      voiceOn: !!voice,
      bgmOcean: bgm.current.ocean,
      bgmMusic: bgm.current.music,
      scale: vrCal.scale,
      reach: vrCal.reach,
      wristDeg: vrCal.wristDeg,
      tiltDeg: vrCal.tiltDeg,
      yawDeg: vrCal.yawDeg,
    });
  }

  // 语音口型：本地音量 → 自己的嘴；10 Hz 同步给对方
  // 只在"正在说话"或"刚说完（补一帧 0）"时发包，避免安静时刷 10 Hz 的空包
  if (voice) {
    const level = voice.level;
    avatar?.setExpression('aa', level * 0.9);
    const shouldSend = level > 0.02 || lastMouthLevel > 0.02;
    if (shouldSend && now - lastMouthSent > 100) {
      lastMouthSent = now;
      lastMouthLevel = level;
      net.sendMouth(level);
    }
  }

  // 远端的插值与表情在帧循环里跑（上行姿态由 startNetworking 的定时器负责）
  selfBubble?.update(dt);
  for (const bubble of remoteBubbles.values()) bubble.update(dt);
  for (const remote of remotes.values()) remote.update(dt);

  // 击掌：两端都在举手、而且离得够近（船上 1 m 内基本就是肩并肩）
  if (now < palmUntil && now < peerPalmUntil && now > clapCooldown && remotes.size === 1) {
    const remote = [...remotes.values()][0]!;
    const mine = avatar?.handPosition(handA) ?? null;
    const theirs = remote.handPosition(handB);
    if (mine && theirs && mine.distanceTo(theirs) < 1.0) {
      clapCooldown = now + 1500;
      palmUntil = 0;
      peerPalmUntil = 0;
      avatar?.pulseExpression('happy', 1.4);
      hud.addChat('（击掌！）', true);
      // 提示对方换表情（对方不做也无所谓，纯粹是氛围）
      net.sendExpr('happy');
    }
  }

  hud.setStats(
    { fps: fpsSmooth, distance: state.distance, islands: visibleIslands, seated: player.isSeated },
    dt,
  );

  // 自拍镜：额外一台相机渲染到纹理。渲染前把头显出来，渲染完再藏回去，
  // 否则镜子里会看到一个"没头"的身体（第一人称为了不看头颅内壁会藏掉头/发）。
  if (mirror && mirror.visible) {
    mirror.onBeforeRender = () => avatar?.setHeadHidden(false);
    mirror.onAfterRender = () => avatar?.setHeadHidden(true);
    mirror.update(renderer, scene);
  }

  renderer.render(scene, rig.camera);
}

// setAnimationLoop 而不是 requestAnimationFrame：进入 WebXR 后由它接管帧循环
renderer.setAnimationLoop(frame);

/* ---------------- 自检钩子（自动化测试与真机排查用）---------------- */
interface DebugInfo {
  fps: number;
  distance: number;
  islands: number;
  drawCalls: number;
  triangles: number;
  waves: number;
  seated: boolean;
  pos: [number, number, number];
  eyeY: number;
}

/** VR 状态自检：相机挂在哪、朝向是否来自头显、模型头是否跟上 */
function xrDebugState(): Record<string, unknown> {
  const cam = rig.camera;
  const camWorld = cam.getWorldPosition(new THREE.Vector3());
  const head = avatar?.headBone ?? null;
  const headWorld = head ? head.getWorldPosition(new THREE.Vector3()) : null;
  return {
    xrActive: player.isXRActive,
    cameraParent: cam.parent?.name ?? null,
    heading: +rig.heading.toFixed(3),
    cameraWorld: camWorld.toArray().map((n) => +n.toFixed(3)),
    cameraLocal: cam.position.toArray().map((n) => +n.toFixed(3)),
    headWorld: headWorld ? headWorld.toArray().map((n) => +n.toFixed(3)) : null,
    headToCamera: headWorld ? +headWorld.distanceTo(camWorld).toFixed(3) : null,
    calibration: {
      eyeHeight: +player.xrCalibration.eyeHeight.toFixed(3),
      scale: +player.xrCalibration.scale.toFixed(3),
      valid: player.xrCalibrationValid,
    },
    seated: player.isSeated,
    avatarRootY: avatar ? +avatar.root.position.y.toFixed(3) : null,
    feet: player.debugFeet(),
  };
}

/**
 * 调试：直接读出「肩 / 肘 / 手」的实际几何关系，用来判断肘到底往哪边弯。
 *
 * perpDir 是肘相对「肩→手连线」的垂直偏移方向，换算到**身体局部空间**：
 * +X = 角色右侧、+Y = 上、+Z = 身体后方。人抬手时这个方向应该是「下 + 后」，
 * 也就是 y<0、z>0；如果变成 y>0（肘朝上/朝前）就是弯曲平面反了。
 */
function ikInfoState(): Record<string, unknown> | null {
  const av = player.currentAvatar;
  const hum = av?.vrm.humanoid;
  if (!av || !hum) return null;
  const root = av.root;
  root.updateWorldMatrix(true, false);
  const bodyQ = root.getWorldQuaternion(new THREE.Quaternion()).invert();

  const sideInfo = (side: 'left' | 'right'): Record<string, unknown> | null => {
    const up = hum.getNormalizedBoneNode(side === 'left' ? 'leftUpperArm' : 'rightUpperArm');
    const low = hum.getNormalizedBoneNode(side === 'left' ? 'leftLowerArm' : 'rightLowerArm');
    const hand = hum.getNormalizedBoneNode(side === 'left' ? 'leftHand' : 'rightHand');
    if (!up || !low || !hand) return null;
    const sh = up.getWorldPosition(new THREE.Vector3());
    const el = low.getWorldPosition(new THREE.Vector3());
    const hd = hand.getWorldPosition(new THREE.Vector3());
    const dir = hd.clone().sub(sh);
    const reach = dir.length();
    dir.normalize();
    const toEl = el.clone().sub(sh);
    const perp = toEl.clone().addScaledVector(dir, -toEl.dot(dir));
    const l1 = sh.distanceTo(el);
    const l2 = el.distanceTo(hd);
    return {
      l1: +l1.toFixed(3),
      l2: +l2.toFixed(3),
      armLength: +(l1 + l2).toFixed(3),
      shoulderToHand: +reach.toFixed(3),
      /** 到臂展的比例：接近 1 说明手臂快伸直了，这时极向量再对也没多少空间 */
      reachRatio: +((l1 + l2) > 0 ? reach / (l1 + l2) : 0).toFixed(3),
      perpLen: +perp.length().toFixed(3),
      perpDirBody: perp.lengthSq() > 1e-9
        ? perp.clone().applyQuaternion(bodyQ).toArray().map((n) => +n.toFixed(3))
        : null,
    };
  };

  return { left: sideInfo('left'), right: sideInfo('right') };
}

(window as unknown as { __sea: unknown }).__sea = {
  state,
  boat,
  rig,
  player,
  ocean,
  renderer,
  scene,
  voyage: () => voyage,
  avatarInfo: () =>
    avatar
      ? { ...avatar.measure, loaded: true, expressions: avatar.expressions }
      : { loaded: false },
  /** 调试：本地播一个表情（blink 也可以用来验证眨眼） */
  face: (name: string, duration = 1.4) => avatar?.pulseExpression(name, duration),
  /** 调试：挥手并回报手心法线与身体正前方的夹角（0° = 正对镜头） */
  waveCheck: () => {
    avatar?.pulseGesture('wave');
  },
  palmInfo: () => {
    const n = avatar?.palmNormalWorld();
    if (!n) return null;
    const yaw = rig.heading;
    const forward = { x: -Math.sin(yaw), y: 0, z: -Math.cos(yaw) };
    const dot = n.x * forward.x + n.z * forward.z;
    return {
      normal: [+n.x.toFixed(2), +n.y.toFixed(2), +n.z.toFixed(2)],
      angleDeg: Math.round((Math.acos(Math.max(-1, Math.min(1, dot))) * 180) / Math.PI),
    };
  },
  netInfo: () => ({
    room: roomCode,
    status: net.status,
    selfId: net.selfId,
    peers: [...remotes.keys()],
    sentPoses: net.sentPoses,
    hidden: document.visibilityState === 'hidden',
    voice: voice ? { state: voice.state, muted: voice.muted, level: +voice.level.toFixed(3), peer: voicePeerId } : null,
  }),
  /** 调试：不依赖头显，走一遍 VR 的挂点切换（相机 → 稳定节点） */
  fakeXR: (on: boolean) => {
    if (on) {
      player.enterXR();
      rig.setXRActive(true);
    } else {
      player.exitXR();
      rig.setXRActive(false);
    }
    return xrDebugState();
  },
  xrInfo: () => xrDebugState(),
  bgmInfo: () => bgm.debugInfo(),
  showHandModels: (on: boolean) => {
    handModelsVisible = on;
    for (const hand of xrHands) hand.traverse((o) => {
      if (o.name === 'hand-model') o.visible = on;
    });
    for (const controller of xrControllers) controller.traverse((o) => {
      if (o.name === 'controller-model') o.visible = on;
    });
    return handModelsVisible;
  },
  mirrorPreview: () => (mirror ? mirror.previewOverlay(renderer) : 'no-mirror'),
  mirrorInfo: () => {
    if (!mirror) return null;
    return {
      visible: mirror.visible,
      centerPixel: mirror.sample(renderer),
    };
  },
  /** 调试：双臂骨架几何（肘往哪边弯、手臂伸展到什么程度） */
  ikInfo: () => ikInfoState(),
  /** 调试：脚部 IK 目标与踝高（甲板局部坐标，y=0 为甲板面） */
  feetInfo: () => player.debugFeet(),
  /** 调试：喂一份假的手柄数据，验证按键映射（不需要头显） */
  xrInputTest: (
    sources: Array<{ handedness: string; axes: number[]; buttons: number[] }>,
  ) => {
    const fake = new XRInput();
    const rendererLike = {
      xr: {
        getSession: () => ({
          inputSources: sources.map((s) => ({
            handedness: s.handedness,
            gamepad: {
              axes: s.axes,
              buttons: s.buttons.map((v) => ({ value: v, pressed: v > 0.5 })),
            },
          })),
        }),
      },
    };
    return fake.update(rendererLike as never);
  },
  snapTurn: (dir: number) => {
    player.snapTurn(dir);
    return xrDebugState();
  },
  /** 调试：给一只手一个世界坐标目标，返回 IK 结果（不需要头显） */
  ikTest: (
    side: 'left' | 'right',
    x: number,
    y: number,
    z: number,
    quat?: [number, number, number, number],
  ) => {
    const av = player.currentAvatar;
    if (!av) return null;
    const target = new THREE.Vector3(x, y, z);
    const handTarget = {
      position: target,
      quaternion: quat ? new THREE.Quaternion(quat[0], quat[1], quat[2], quat[3]) : null,
    };
    av.setHandTargets(
      side === 'left' ? { left: handTarget, right: null } : { left: null, right: handTarget },
    );
    av.update(1 / 60);

    const humanoid = av.vrm.humanoid;
    const bones =
      side === 'left'
        ? { up: 'leftUpperArm', low: 'leftLowerArm', hand: 'leftHand' }
        : { up: 'rightUpperArm', low: 'rightLowerArm', hand: 'rightHand' };
    const hand = humanoid?.getNormalizedBoneNode(bones.hand as never);
    const elbow = humanoid?.getNormalizedBoneNode(bones.low as never);
    const shoulder = humanoid?.getNormalizedBoneNode(bones.up as never);
    if (!hand) return null;
    const handPos = hand.getWorldPosition(new THREE.Vector3());
    const shoulderPos = shoulder ? shoulder.getWorldPosition(new THREE.Vector3()) : null;
    const hq = av.handTransform(side)?.quaternion ?? null;
    // 手指实际指向：用中指近节相对手掌的位置来量（不依赖骨骼轴向假设）
    const middle = humanoid?.getNormalizedBoneNode(
      (side === 'left' ? 'leftMiddleProximal' : 'rightMiddleProximal') as never,
    );
    const fingerDir = middle
      ? middle.getWorldPosition(new THREE.Vector3()).sub(handPos).normalize().toArray().map((n) => +n.toFixed(3))
      : null;
    return {
      target: target.toArray().map((n) => +n.toFixed(3)),
      hand: handPos.toArray().map((n) => +n.toFixed(3)),
      error: +handPos.distanceTo(target).toFixed(3),
      shoulder: shoulderPos ? shoulderPos.toArray().map((n) => +n.toFixed(3)) : null,
      shoulderY: shoulderPos ? +shoulderPos.y.toFixed(3) : null,
      elbowY: elbow ? +elbow.getWorldPosition(new THREE.Vector3()).y.toFixed(3) : null,
      handQuat: hq ? [+hq.x.toFixed(3), +hq.y.toFixed(3), +hq.z.toFixed(3), +hq.w.toFixed(3)] : null,
      fingerDir,
      palmDir: (() => {
        const n = av.palmNormalWorld();
        return n ? n.toArray().map((v) => +v.toFixed(3)) : null;
      })(),
    };
  },
  /** 调试：清掉手柄目标，回到程序化姿态 */
  clearHandTargets: () => player.setHandTargets(null),
  /** 调试：不依赖麦克风，验证 WebRTC 在本浏览器里能不能跑通（振荡器 → 两条连接互连） */
  voiceLoopback: async () => {
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const dest = ctx.createMediaStreamDestination();
    osc.connect(dest);
    osc.start();
    const a = new RTCPeerConnection();
    const b = new RTCPeerConnection();
    a.onicecandidate = (e) => void (e.candidate && b.addIceCandidate(e.candidate));
    b.onicecandidate = (e) => void (e.candidate && a.addIceCandidate(e.candidate));
    for (const track of dest.stream.getTracks()) a.addTrack(track, dest.stream);
    let gotTrack = false;
    b.ontrack = () => {
      gotTrack = true;
    };
    const offer = await a.createOffer();
    await a.setLocalDescription(offer);
    await b.setRemoteDescription(offer);
    const answer = await b.createAnswer();
    await b.setLocalDescription(answer);
    await a.setRemoteDescription(answer);
    await new Promise((r) => setTimeout(r, 800));
    const states = { a: a.connectionState, b: b.connectionState };
    a.close();
    b.close();
    osc.stop();
    await ctx.close();
    return { gotTrack, ...states };
  },
  seek(distance: number): void {
    state.distance = distance;
    voyage.ensure(distance + FOG.far);
  },
  info(): DebugInfo {
    const p = player.root.position;
    return {
      fps: Math.round(fpsSmooth),
      distance: Math.round(state.distance),
      islands: islands.activeCount,
      drawCalls: renderer.info.render.calls,
      triangles: renderer.info.render.triangles,
      waves: waves.length,
      seated: player.isSeated,
      pos: [Number(p.x.toFixed(2)), Number(p.y.toFixed(2)), Number(p.z.toFixed(2))],
      eyeY: Number(rig.camera.getWorldPosition(new THREE.Vector3()).y.toFixed(2)),
    };
  },
};
